import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { message, Button } from 'antd';
import { Tooltip, useMediaQuery } from '@mui/material';
import { EditOutlined } from '@ant-design/icons';
import { postSendMessageV2, getChatMessagesV2, clearChatMessagesV2 } from "../../../api/chat";
import { getAvailableModelsV2 } from "../../../api/models";
import './Chat.css';
import logger from '../../../utils/logger';
import { analytics } from '../../../firebaseConfig';
import { logEvent } from 'firebase/analytics';
import { getSourceByCategory } from './utils/chatUtils';
import { useConceptGate } from '../../../context/ConceptGateContext';

// Import components
import ChatMessages from './components/ChatMessages';
import ChatFooter from './components/ChatFooter';
import StreamingChat from './components/StreamingChat';
import WelcomeCard from '../MiddlePanel/WelcomeCard';

const Chat = forwardRef(({
    auth,
    selectedInstrument,
    selectedCategory,
    isDarkMode,
    selectedChatId,
    isChatExpanded,
    onChatToggle,
    addLine,
    onDisabledInputClick,
    onDetach,
    isDetached = false
}, ref) => {
    const isMobile = useMediaQuery('(max-width:768px)');
    const userInfo = useSelector(state => state.user.userInfo);
    const { refreshTrade } = useConceptGate();
    const [messages, setMessages] = useState([]);
    const [isChatLoading, setIsChatLoading] = useState(false);
    const chatWindowRef = useRef(null);
    const inputFieldRef = useRef(null);
    const [selectedModel, setSelectedModel] = useState(() => {
        const savedModel = localStorage.getItem('ntb:copilot:selectedModel');
        return savedModel || 'gpt-4o-mini';
    });
    const [aiModels, setAiModels] = useState([
        { value: 'gpt-4o-mini', label: 'gpt-4o-mini', isPremium: false, disabled: false }
    ]);
    const [inputText, setInputText] = useState("");
    const [isStreaming, setIsStreaming] = useState(false);
    const [isStreamingMode] = useState(() => {
        // Always default to true (streaming enabled), but allow admin to change it
        const savedMode = localStorage.getItem('ntb:copilot:streamingMode');
        return savedMode === null ? true : savedMode === 'true';
    });

    // State for thinking/reasoning display
    const [thinkingState, setThinkingState] = useState({
        isThinking: false,
        thinkingId: null,
        content: '',
        durationMs: null,
        summary: null
    });

    // State for tool executions
    const [toolExecutions, setToolExecutions] = useState({});

    // Handler for thinking updates from StreamingChat
    const handleThinkingUpdate = useCallback((update) => {
        if (update.type === 'start') {
            setThinkingState({
                isThinking: true,
                thinkingId: update.thinkingId,
                content: '',
                durationMs: null,
                summary: null
            });
        } else if (update.type === 'delta') {
            setThinkingState(prev => ({
                ...prev,
                content: update.content
            }));
        } else if (update.type === 'end') {
            setThinkingState(prev => ({
                ...prev,
                isThinking: false,
                durationMs: update.durationMs,
                summary: update.summary
            }));
        }
    }, []);

    // Handler for tool updates from StreamingChat
    const handleToolUpdate = useCallback((update) => {
        setToolExecutions(prev => ({
            ...prev,
            [update.toolId]: {
                toolId: update.toolId,
                toolName: update.toolName,
                status: update.status,
                arguments: update.arguments,
                result: update.result,
                durationMs: update.durationMs
            }
        }));

        // When extract_trade_spec completes, refresh the ConceptGate panel
        if (update.toolName === 'extract_trade_spec' && update.type === 'end' && update.status === 'success') {
            // Parse instrument from arguments to refresh the right trade
            try {
                const args = typeof update.arguments === 'string' ? JSON.parse(update.arguments) : update.arguments;
                if (args?.instrument && refreshTrade) {
                    // Small delay to ensure backend has saved the data
                    setTimeout(() => refreshTrade(args.instrument), 500);
                }
            } catch (e) {
                // If we can't parse args, refresh current instrument
                if (refreshTrade) {
                    setTimeout(() => refreshTrade(), 500);
                }
            }
        }
    }, [refreshTrade]);

    // Reset thinking and tools when chat changes or streaming starts
    const resetAgentState = useCallback(() => {
        setThinkingState({
            isThinking: false,
            thinkingId: null,
            content: '',
            durationMs: null,
            summary: null
        });
        setToolExecutions({});
    }, []);

    const focusInputField = useCallback(() => {
        if (!isMobile && inputFieldRef.current) {
            inputFieldRef.current.focus();
        }
    }, [isMobile]);

    // Add effect to save selected model
    useEffect(() => {
        localStorage.setItem('ntb:copilot:selectedModel', selectedModel);
    }, [selectedModel]);

    // Add effect to save streaming mode
    useEffect(() => {
        localStorage.setItem('ntb:copilot:streamingMode', isStreamingMode);
        
        // Clean up streaming connection when streaming mode is disabled
        if (!isStreamingMode && streamingChatRef.current) {
            streamingChatRef.current.cleanupStreamingConnection?.();
        }
    }, [isStreamingMode]);

    // Track chat session metrics
    useEffect(() => {
        const sessionStart = Date.now();
        
        return () => {
            const sessionDuration = Date.now() - sessionStart;
            logEvent(analytics, 'chat_session_metrics', {
                chat_id: selectedChatId,
                session_duration: sessionDuration,
                message_count: messages.length,
                instrument: selectedInstrument,
                category: selectedCategory
            });
        };
        //  eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedChatId]);

    // Initialize StreamingChat
    const streamingChatRef = useRef(null);
    
    // Memoized handlers
    const handleSendMessage = useCallback((text) => {
        if (!selectedChatId) {
            message.warning('Please select an instrument first');
            return;
        }
        
        if (text) {
            const messageStartTime = Date.now();
            
            // Log message sending attempt
            logEvent(analytics, 'chat_message_sent', {
                chat_id: selectedChatId,
                message_length: text.length,
                instrument: selectedInstrument,
                category: selectedCategory,
                streaming_mode: isStreamingMode
            });

            let metadata = {
                symbol: selectedInstrument,
                timestamp: Date.now(),
                category: selectedCategory,
                model: selectedModel,
                source: getSourceByCategory(selectedCategory)
            };

            const newMessage = { 
                text, 
                sender: "user",
                timestamp: new Date().toISOString(),
                model: selectedModel
            };

            setMessages(prev => [...prev, newMessage]);
            setIsChatLoading(true);

            if (isStreamingMode && streamingChatRef.current) {
                // Use the StreamingChat component to handle streaming
                streamingChatRef.current.sendStreamingMessage(text, messageStartTime);
            } else {
                // Legacy mode
                postSendMessageV2(auth, (response) => {
                    if (response && response.response) {
                        // Log successful message send
                        logEvent(analytics, 'chat_message_success', {
                            chat_id: selectedChatId,
                            message_length: text.length,
                            response_time: Date.now() - messageStartTime,
                            instrument: selectedInstrument,
                            category: selectedCategory,
                            streaming_mode: false
                        });

                        setMessages(prev => [
                            ...prev,
                            { 
                                text: response.response, 
                                sender: "assistant",
                                timestamp: new Date().toISOString(),
                                model: selectedModel
                            }
                        ]);

                        if (response.metadata && response.metadata.lines) {
                            setMessages(prev => [
                                ...prev,
                                { 
                                    text: JSON.stringify({ lines: response.metadata.lines }), 
                                    sender: "processing",
                                    timestamp: new Date().toISOString(),
                                    model: selectedModel
                                }
                            ]);    
                        }
                    }
                    setIsChatLoading(false);
                    // Focus the input field after response is received
                    focusInputField();
                }, selectedChatId, text, null, metadata, selectedModel);
            }
        }
    }, [selectedChatId, selectedInstrument, selectedCategory, selectedModel, auth, isStreamingMode, focusInputField]);

    useEffect(() => {
        if (auth) {
            logger.log("userInfo", userInfo);
            getAvailableModelsV2(auth, (data) => {
                // Check if data is an array (direct model list) or has models property
                const modelsList = Array.isArray(data) ? data : data.models || [];
                
                if (modelsList.length > 0) {
                    // Transform the models data into the format needed for Select
                    const availableModels = modelsList
                        .filter(model => userInfo?.admin || model.modelEnabled) // Show disabled models only for admins
                        .map(model => ({
                            value: model.modelId,
                            label: `${!model.modelEnabled ? '(D) ' : ''}${model.modelName}${model.premium ? ' 🌟' : ''}`,
                            isPremium: model.premium,
                            provider: model.provider || 'openai',
                            disabled: userInfo?.admin ? false : (!model.modelEnabled || (model.premium && !userInfo?.premium))
                        }));
                    
                    logger.log("Available models transformed:", availableModels);
                    setAiModels(availableModels);
                    
                    // Check if selectedModel is in availableModels and not disabled
                    const currentModelOption = availableModels.find(model => model.value === selectedModel);
                    if (currentModelOption && !currentModelOption.disabled) {
                        setSelectedModel(selectedModel);
                    } else {
                        // Set default model to first available non-disabled model
                        const firstAvailableModel = availableModels.find(model => !model.disabled);
                        if (firstAvailableModel) {
                            setSelectedModel(firstAvailableModel.value);
                        }
                    }
                } else {
                    logger.warn("No models returned from API or empty models list:", data);
                }
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth, userInfo]);

    useEffect(() => {
        if (selectedChatId) {
            // Skip loading messages for temporary chat IDs (optimistic UI)
            // Temporary chats start with "temp-" and don't exist on the backend yet
            if (selectedChatId.startsWith('temp-')) {
                setMessages([]);
                setIsChatLoading(false);
                return;
            }
            
            // Show loading state
            setIsChatLoading(true);
            
            // Define and execute fetchChatMessages inside useEffect
            const fetchChatMessages = () => {
                setIsChatLoading(true);
                getChatMessagesV2(auth, (data) => {
                    if (data.error) {
                        logger.error('Error fetching chat messages:', data.error);
                        message.error('Failed to load chat messages');
                        setMessages([]);
                    } else {
                        setMessages(data.map(msg => ({
                            text: msg.message,
                            sender: msg.sender,
                            timestamp: msg.created_at
                        })));
                    }
                    setIsChatLoading(false);
                }, selectedChatId);
            };

            fetchChatMessages();
        } else {
            // Clear messages when no chat is selected
            setMessages([]);
            setIsChatLoading(false);
        }
    }, [selectedChatId, isDarkMode, auth]); // Added auth as a dependency

    // Smart scroll - only auto-scroll if user is near the bottom
    const scrollToBottom = useCallback((smooth = true) => {
        if (chatWindowRef.current) {
            const { scrollHeight, scrollTop, clientHeight } = chatWindowRef.current;
            const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

            // Always scroll for new user messages or if near bottom
            if (isNearBottom || !messages.length) {
                chatWindowRef.current.scrollTo({
                    top: scrollHeight,
                    behavior: smooth ? 'smooth' : 'auto'
                });
            }
        }
    }, [messages.length]);

    useEffect(() => {
        // Scroll to bottom whenever messages change or when loading state changes
        scrollToBottom(true);
    }, [messages, isChatLoading, scrollToBottom]);

    // Focus input field when chat loading state changes from true to false
    useEffect(() => {
        if (!isChatLoading) {
            focusInputField();
        }
    }, [isChatLoading, focusInputField]);

    // Simplify sendMessage in useImperativeHandle
    useImperativeHandle(ref, () => ({
        sendMessage: (text) => {
            if (text && text.trim() !== "" && selectedChatId) {
                sendMessageToChat(selectedChatId, text);
            }
        },
        addMessage,
        getCurrentChatId: () => selectedChatId
    }));

    // Update sendMessageToChat to handle lines as well
    const sendMessageToChat = (chatId, text) => {
        let newMessage = { 
            text: text, 
            sender: "user",
            timestamp: new Date().toISOString(),
            model: selectedModel
        };

        setMessages(prevMessages => [...prevMessages, newMessage]);
        setIsChatLoading(true);

        let metadata = {
            symbol: selectedInstrument,
            timestamp: Date.now(),
            category: selectedCategory,
            model: selectedModel,
            source: getSourceByCategory(selectedCategory)
        };

        postSendMessageV2(auth, (response) => {
            if (response && response.response) {
                setMessages(prevMessages => [
                    ...prevMessages,
                    { 
                        text: response.response, 
                        sender: "assistant",
                        timestamp: new Date().toISOString(),
                        model: selectedModel,
                        metadata: response.metadata
                    }
                ]);
            } else {
                setMessages(prevMessages => [
                    ...prevMessages,
                    { 
                        text: "Sorry, I couldn't process your request.", 
                        sender: "assistant",
                        timestamp: new Date().toISOString(),
                        model: selectedModel
                    }
                ]);
            }
            setIsChatLoading(false);
            // Focus the input field after response is received
            focusInputField();
        }, chatId, newMessage.text, null, metadata);
    };

    // Add this new method to the Chat component
    const addMessage = (message) => {
        setMessages(prevMessages => [...prevMessages, message]);
    };

    // Add this new function in the Chat component
    const handleClearChat = () => {
        // Log chat clear
        logEvent(analytics, 'chat_cleared', {
            chat_id: selectedChatId,
            message_count: messages.length,
            instrument: selectedInstrument,
            category: selectedCategory
        });

        clearChatMessagesV2(auth, (response) => {
            if (response && !response.error) {
                setMessages([]);
            }
        }, selectedChatId);
    };

    // Add new handler for question clicks
    const handleQuestionClick = useCallback((question) => {
        setInputText(question);
        focusInputField();
    }, [focusInputField]);

    // Handle stop generation
    const handleStopGeneration = useCallback(() => {
        if (streamingChatRef.current?.cleanupStreamingConnection) {
            streamingChatRef.current.cleanupStreamingConnection();
        }
        setIsStreaming(false);
        setIsChatLoading(false);
        logEvent(analytics, 'generation_stopped', {
            chat_id: selectedChatId,
            instrument: selectedInstrument
        });
    }, [selectedChatId, selectedInstrument]);


    // Add effect to clean up streaming when component unmounts or chat changes
    useEffect(() => {
        return () => {
            if (streamingChatRef.current && streamingChatRef.current.cleanupStreamingConnection) {
                // eslint-disable-next-line react-hooks/exhaustive-deps
                streamingChatRef.current.cleanupStreamingConnection();
            }
        };
    }, [selectedChatId]);

    const showMobileWelcome = isMobile && !selectedInstrument;

    return (
        <div className={`ntb-chat-section ${isDarkMode ? 'dark' : 'light'}`}>
            <div className="ntb-chat-container">
                {/* Hover button for new chat - appears on left side, fixed position */}
                {!showMobileWelcome && messages.length > 0 && (
                    <Tooltip title="New chat" placement="right" arrow>
                        <span>
                            <Button
                                type="text"
                                icon={<EditOutlined />}
                                className="ntb-chat-new-chat-button"
                                onClick={handleClearChat}
                                aria-label="New chat"
                            />
                        </span>
                    </Tooltip>
                )}
                {showMobileWelcome ? (
                    <WelcomeCard
                        isDarkMode={isDarkMode}
                        onQuickStartClick={() => {}}
                        disableQuickStart
                    />
                ) : (
                    <>
                        <ChatMessages
                            messages={messages}
                            isChatLoading={isChatLoading}
                            selectedInstrument={selectedInstrument}
                            selectedCategory={selectedCategory}
                            selectedChatId={selectedChatId}
                            onQuestionClick={handleQuestionClick}
                            addLine={addLine}
                            chatWindowRef={chatWindowRef}
                            thinkingState={thinkingState}
                            toolExecutions={toolExecutions}
                        />
                        <ChatFooter
                            onSendMessage={handleSendMessage}
                            disabled={isChatLoading || !selectedChatId}
                            selectedModel={selectedModel}
                            onModelChange={setSelectedModel}
                            aiModels={aiModels}
                            isChatLoading={isChatLoading}
                            inputText={inputText}
                            onInputChange={setInputText}
                            selectedChatId={selectedChatId}
                            selectedInstrument={selectedInstrument}
                            selectedCategory={selectedCategory}
                            inputRef={inputFieldRef}
                            onDisabledInputClick={onDisabledInputClick}
                            onStopGeneration={handleStopGeneration}
                            isStreaming={isStreaming}
                        />
                    </>
                )}
            </div>
            {isStreamingMode && (
                <StreamingChat
                    ref={streamingChatRef}
                    auth={auth}
                    selectedChatId={selectedChatId}
                    selectedInstrument={selectedInstrument}
                    selectedCategory={selectedCategory}
                    selectedModel={selectedModel}
                    onMessageUpdate={setMessages}
                    onStreamingStatusChange={(status) => {
                        if (status === 'connecting') {
                            resetAgentState();
                        }
                    }}
                    onStreamingComplete={() => {
                        setIsStreaming(false);
                        setIsChatLoading(false);
                        // Mark any remaining running tools as complete
                        setToolExecutions(prev => {
                            const updated = { ...prev };
                            Object.keys(updated).forEach(toolId => {
                                if (updated[toolId].status === 'running' || updated[toolId].status === 'pending') {
                                    updated[toolId] = {
                                        ...updated[toolId],
                                        status: 'success'
                                    };
                                }
                            });
                            return updated;
                        });
                        // Focus the input field after streaming is complete
                        focusInputField();
                    }}
                    onStreamingError={() => {
                        setIsStreaming(false);
                        setIsChatLoading(false);
                        // Mark any remaining running tools as error
                        setToolExecutions(prev => {
                            const updated = { ...prev };
                            Object.keys(updated).forEach(toolId => {
                                if (updated[toolId].status === 'running' || updated[toolId].status === 'pending') {
                                    updated[toolId] = {
                                        ...updated[toolId],
                                        status: 'error'
                                    };
                                }
                            });
                            return updated;
                        });
                        // Focus the input field if there's an error
                        focusInputField();
                    }}
                    onThinkingUpdate={handleThinkingUpdate}
                    onToolUpdate={handleToolUpdate}
                />
            )}
        </div>
    );
});

Chat.displayName = 'Chat';

export default Chat;
